import { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { X, Search, Calendar, User, MapPin } from 'lucide-react';
import { JobCard } from '../../types/jobcard';
import { searchLinkableJobCards } from '../../services/jobcardApi';
import { showErrorToast } from '../../utils/toast';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    poJobCard: JobCard;
    onSelectJobCard: (jobCard: JobCard) => void;
};

export default function LinkJobCardModal({ isOpen, onClose, poJobCard, onSelectJobCard }: Props) {
    const [searchTerm, setSearchTerm] = useState('');
    const [linkableJobCards, setLinkableJobCards] = useState<JobCard[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedJobCard, setSelectedJobCard] = useState<JobCard | null>(null);

    useEffect(() => {
        if (isOpen) {
            fetchLinkableJobCards();
        }
    }, [isOpen]);

    const fetchLinkableJobCards = async (search?: string) => {
        try {
            setIsLoading(true);
            const response = await searchLinkableJobCards(poJobCard.id, search);
            setLinkableJobCards(response.data.data);
        } catch (error: unknown) {
            console.error('Error fetching linkable job cards:', error);
            let message = 'An unknown error occurred.';
            if (error instanceof Error) {
                const axiosMessage = (error as { response?: { data?: { message?: string } } }).response?.data?.message;
                message = axiosMessage || error.message;
            }
            showErrorToast('Failed to fetch linkable job cards: ' + message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        fetchLinkableJobCards(searchTerm);
    };

    const handleSelectJobCard = (jobCard: JobCard) => {
        if (selectedJobCard && selectedJobCard.id === jobCard.id) {
            setSelectedJobCard(null);
        } else {
            setSelectedJobCard(jobCard);
        }
    };

    const handleClearSelection = () => {
        setSelectedJobCard(null);
    };

    const handleConfirmSelection = () => {
        if (selectedJobCard) {
            onSelectJobCard(selectedJobCard);
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString();
    };

    const formatTime = (timeString: string) => {
        return timeString ? timeString.substring(0, 5) : '';
    };

    return (
        <Dialog open={isOpen} onClose={onClose} className="relative z-50">
            <div className="fixed inset-0 bg-black/50" aria-hidden="true" />

            <div className="fixed inset-0 flex items-center justify-center p-4">
                <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 text-left align-middle shadow-xl transition-all max-h-[90vh] flex flex-col">
                    <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
                        <div>
                            <Dialog.Title className="text-lg font-medium text-gray-900 dark:text-white">
                                Link to Completed Job Card
                            </Dialog.Title>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                ATR Job Card: #{poJobCard.job_number} - Order #{poJobCard.order_number}
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
                        >
                            <X size={24} />
                        </button>
                    </div>

                    <div className="p-6 flex-1 overflow-hidden flex flex-col">
                        {/* Search Form */}
                        <form onSubmit={handleSearch} className="mb-6">
                            <div className="flex gap-2">
                                <div className="flex-1 relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                                    <input
                                        type="text"
                                        placeholder="Search by job number or technician name..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                                >
                                    Search
                                </button>
                            </div>
                        </form>

                        {/* Suggested Matches */}
                        <div className="flex-1 overflow-hidden flex flex-col">
                            <h3 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                                Suggested Matches ({linkableJobCards.length} found)
                            </h3>
                            
                            {isLoading ? (
                                <div className="flex-1 flex items-center justify-center">
                                    <div className="text-gray-500 dark:text-gray-400">Loading...</div>
                                </div>
                            ) : linkableJobCards.length === 0 ? (
                                <div className="flex-1 flex items-center justify-center">
                                    <div className="text-center text-gray-500 dark:text-gray-400">
                                        <p>No matching completed job cards found.</p>
                                        <p className="text-sm mt-1">
                                            Criteria: Same store, completed, no order number, within 2 months
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex-1 overflow-y-auto space-y-3">
                                    {linkableJobCards.map((jobCard) => (
                                        <div
                                            key={jobCard.id}
                                            onClick={() => handleSelectJobCard(jobCard)}
                                            className={`p-4 border rounded-lg cursor-pointer transition-all ${
                                                selectedJobCard?.id === jobCard.id
                                                    ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30'
                                                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                                            }`}
                                        >
                                            <div className="flex justify-between items-start">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <span className="font-semibold text-gray-900 dark:text-white">
                                                            #{jobCard.job_number}
                                                        </span>
                                                        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100 rounded-full">
                                                            Completed
                                                        </span>
                                                    </div>
                                                    
                                                    <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 dark:text-gray-400">
                                                        <div className="flex items-center gap-2">
                                                            <User size={16} />
                                                            <span>{jobCard.user?.name || 'Unknown'}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <MapPin size={16} />
                                                            <span>{jobCard.store?.name || 'Unknown Store'}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <Calendar size={16} />
                                                            <span>{jobCard.date ? formatDate(jobCard.date) : 'No date'}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <span>Time: {formatTime(jobCard.time_in || '')} - {formatTime(jobCard.time_out || '')}</span>
                                                        </div>
                                                    </div>

                                                    {jobCard.tasks && jobCard.tasks.length > 0 && (
                                                        <div className="mt-2">
                                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                                Tasks: {jobCard.tasks.length}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                                
                                                {selectedJobCard?.id === jobCard.id && (
                                                    <div className="ml-4">
                                                        <div className="w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center">
                                                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                            </svg>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Selected Job Card */}
                        {selectedJobCard && (
                            <div className="mb-4 p-4 bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-700 rounded-lg">
                                <div className="flex justify-between items-start mb-2">
                                    <h4 className="font-medium text-indigo-900 dark:text-indigo-100">Selected Job Card</h4>
                                    <button
                                        onClick={handleClearSelection}
                                        className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-200 text-sm"
                                    >
                                        Clear Selection
                                    </button>
                                </div>
                                <div className="text-sm text-indigo-800 dark:text-indigo-200">
                                    <p><strong>Job #:</strong> {selectedJobCard.job_number}</p>
                                    <p><strong>Date:</strong> {selectedJobCard.date ? formatDate(selectedJobCard.date) : 'N/A'}</p>
                                    <p><strong>Time:</strong> {formatTime(selectedJobCard.time_in ?? '')} - {formatTime(selectedJobCard.time_out ?? '')}</p>
                                    <p><strong>Technician:</strong> {selectedJobCard.user?.name}</p>
                                    <p><strong>Tasks:</strong> {selectedJobCard.tasks?.length || 0}</p>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleConfirmSelection}
                            disabled={!selectedJobCard}
                            className="px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Continue with Selected Job Card
                        </button>
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
} 